// src/app/features/taxExtensions/CreateTaxExtension.tsx
import { DashboardContainerHeader } from 'app/layout/dashboardLayout/BoxLayout';
import { useCreateTaxExtensionMutation } from 'app/services/taxExtensions';
import { SaveIcon } from 'assets/icons';
import Button from 'Components/Buttons/Button';
import { successToast, errorToast } from 'Components/Notifications';
import { useAuth } from 'utils/AuthProvider';
import TaxExtensionForm from './TaxExtensionForm';
import { useNavigate } from 'react-router-dom';
import { prepareHeaders } from 'utils'; // Assuming this adds authorization token
import { useGetUserBusinessesQuery } from 'app/services/user';

export default function CreateTaxExtension() {
  const [CreateTrigger, { isLoading }] = useCreateTaxExtensionMutation();
  const { user } = useAuth();
  const navigate = useNavigate();
  // Use existing hook to get all businesses for the user
  const { data: businesses } = useGetUserBusinessesQuery();

  // Create the tax extension and initiate the payment
  const handleCreateAndPay = async (data: any) => {
    try {
      // Set the default price based on type
      let extension_price = 169.99; // Default price for business
      if (data.type === 'individual') {
        extension_price = 119.99; // Price for individual
      } else if (data.type === 'business' && data.business_id) {
        // Use the businesses hook to find the selected business
        const selectedBusiness = businesses?.find(
          (b) => b.id.toString() === data.business_id.toString(),
        );
        // If the business is TAX subscribed, set the extension price to 0.
        if (selectedBusiness?.subscribed?.TAX) {
          extension_price = 0;
        }
      }

      // Add the price to the data object
      const requestData = {
        ...data,
        extension_price,
      };

      const res = await CreateTrigger(requestData).unwrap();
      successToast('Tax extension request submitted');

      const requestId = res.id; // Assuming the response contains the tax extension request ID

      if (extension_price > 0) {
        // Prepare headers with authorization token
        const headers = await prepareHeaders(new Headers());
        headers.append('Content-Type', 'application/json');

        // Call the payment API
        const paymentResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/tax_extensions_requests/${requestId}/pay`,
          {
            method: 'POST',
            headers,
          },
        );

        const paymentData = await paymentResponse.json();

        if (paymentData.checkout_url) {
          // Redirect to Stripe checkout URL
          window.location.href = paymentData.checkout_url;
        } else {
          errorToast('Payment initiation failed');
        }
      } else {
        // If price is 0, mark as paid directly and redirect back to the tax extensions page
        successToast('Tax extension request submitted and marked as paid');
        navigate('/dashboard/taxExtensions');
      }
    } catch (error) {
      errorToast('Failed to create tax extension request');
    }
  };

  return (
    <>
      <DashboardContainerHeader title={'Tax Extension Request'}>
        <Button
          isLoading={isLoading}
          form="tax-extension-form"
          className="max-w-min"
        >
          <SaveIcon className="w-4 h-4" />
          Create
        </Button>
      </DashboardContainerHeader>
      <TaxExtensionForm
        value={{
          type: 'individual', // Default the type for individual users
          firstName: user?.given_name,
          lastName: user?.family_name,
          email: user?.email,
          ssn: user?.ssn,
          haveDependents: user?.dependents ? 'yes' : 'no',
          isMarried: user?.spouse ? 'yes' : 'no',
          spouseFirstName: user?.spouse?.firstname || '',
          spouseLastName: user?.spouse?.lastname || '',
          spouseSSN: user?.spouse?.ssn || '',
          phoneNumber: user?.phone_number,
          reachPhoneNumber: user?.phone_number,
          address: user?.address,
          city: user?.city,
          state: user?.state,
          zipCode: user?.zipcode,
          // New individual-specific defaults (set as empty strings or 0)
          completedFederalReturn: '',
          filingStatus: '',
          priorYearAGI: 0,
          expectOweTaxes: '',
          estimatedPaymentsMade: '',
          estimatedPaymentsAmount: 0,
          estimatedTaxLiability: 0,
          stateTaxExtensionRequired: '',
          extraordinaryCircumstances: '',
          circumstancesDescription: '',
          assistanceWithTaxPayments: '',
          extensionFilingMethod: '',
          contactInformation: '',
        }}
        onChange={(data) => handleCreateAndPay(data)}
        isLoading={isLoading}
      />
    </>
  );
}
